import { useState, type ReactNode } from "react";
import { Box, Stack, Typography } from "@mui/material";
import ParkIcon from "@mui/icons-material/Park";
import RainforestTreeIcon from "components/icons/RainforestTreeIcon";
import CarSelectReference from "components/cars/CarSelectReference";

interface Props {
  treeCount: number;
  distanceKm: number;
  distanceCaption?: string;
  showCarControls?: boolean;
  /** Optional control anchored to the top-right (e.g. emission disclaimer). */
  topRightAction?: ReactNode;
}

const TripRainforestTreeWidget = ({
  treeCount,
  distanceKm,
  distanceCaption,
  showCarControls = false,
  topRightAction,
}: Props) => {
  const [carInputValue, setCarInputValue] = useState("");
  const formattedCount =
    treeCount >= 10 ? treeCount.toFixed(1) : treeCount.toFixed(2);

  return (
    <Box
      sx={{
        position: "relative",
        mt: 0,
        px: 2,
        py: 1.75,
        borderRadius: 2,
        bgcolor: "success.main",
        color: "common.white",
        boxShadow: "0 2px 8px rgba(46, 125, 50, 0.25)",
      }}
    >
      {topRightAction && (
        <Box
          sx={{
            position: "absolute",
            top: 6,
            right: 6,
            zIndex: 1,
          }}
        >
          {topRightAction}
        </Box>
      )}
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Stack direction="row" spacing={-0.5} alignItems="flex-end">
          <RainforestTreeIcon sx={{ fontSize: 44, opacity: 0.95 }} />
          <ParkIcon sx={{ fontSize: 28, opacity: 0.85, mb: 0.25 }} />
        </Stack>
        <Box flex={1} minWidth={0} pr={topRightAction ? 4 : 0}>
          <Stack
            direction="row"
            alignItems="baseline"
            spacing={0.75}
            flexWrap="wrap"
            useFlexGap
          >
            <Typography
              variant="h4"
              fontWeight={800}
              lineHeight={1}
              letterSpacing={-0.5}
            >
              {formattedCount}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              lineHeight={1.3}
              sx={{ opacity: 0.95 }}
            >
              rainforest trees
            </Typography>
          </Stack>
          <Typography variant="caption" sx={{ opacity: 0.85, lineHeight: 1.35 }}>
            yearly CO₂ absorption equivalent
          </Typography>
          <Typography
            variant="caption"
            sx={{ opacity: 0.75, lineHeight: 1.35, display: "block", mt: 0.25 }}
          >
            {distanceCaption ?? `${Math.round(distanceKm)} km total`}
          </Typography>
        </Box>
      </Stack>

      {showCarControls && (
        <Box
          mt={1.5}
          pt={1.5}
          sx={{
            borderTop: "1px solid",
            borderColor: "rgba(200, 230, 201, 0.45)",
          }}
        >
          <Box
            sx={{
              bgcolor: "rgba(232, 245, 233, 0.95)",
              color: "success.dark",
              borderRadius: 1.5,
              px: 1,
              py: 0.75,
              border: "1px solid",
              borderColor: "rgba(165, 214, 167, 0.7)",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35)",
              "& .MuiTypography-root": {
                color: "success.dark",
              },
              "& .MuiTypography-colorTextSecondary": {
                color: "rgba(27, 94, 32, 0.72) !important",
              },
              "& .MuiSvgIcon-root": {
                color: "success.main",
              },
            }}
          >
            <CarSelectReference
              size="small"
              embedded
              onSearchCar={() => undefined}
              onInputChange={setCarInputValue}
              inputValue={carInputValue}
              carSource="global"
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TripRainforestTreeWidget;
